import { useEffect, useState } from "react";
import { useRef } from "react";
import { getWeek, format } from "date-fns";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/mc-loading-indicator";
import {
  extractDate,
  formatDateAndTime,
  formatHeader,
  transformValidationStatusData,
} from "../../utils/helper";
import { useStore } from "../../store/store.context";
import { defaultSummaryTableHeader, SUMMARY } from "../../constants/constants";
import MessageList from "../../components/message-list/message-list";
import Dashboard from "../../components/dashboard/dashboard";
import CaseSummary from "../../components/case-summary/case-summary";
import PromptPanel from "../../components/prompts/prompt-panel";
import CustomTable from "../../components/common/custom-table/custom-table";
import CaseDetails from "../../components/case-details/case-details";
import AdvancedCaseSearch from "../../components/advanced-case-search/advanced-case-search";
import { McSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-select";
import { McOption } from "@maersk-global/mds-react-wrapper/components-core/mc-option";
import "./home.scss";
import MetricsSnapshot from "../../components/metrics-snapshot/metrics-snapshot";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";


const Home = () => {
  const [apiState, setApiState] = useState({
    caseSummaryData: null,
    donutData: null,
    rows: null,
    headers: null,
    date: null,
    responseData: null,
    dataType: null,
  });



  const { store, updateStore } = useStore();
  const { apiResponseData } = store; 
  const [countryOptions,setCountryOptions] = useState([]);
  const [slaStatusOptions, setSLAStatusOptions] = useState([]);
  const [createdDateOptions, setCreatedDateOptions] = useState([]);
  const [countrySLATimeOptions, setCountrySLATimeOptions] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [filters, setFilters] = useState({ week: "", });
  const [filterTableState, setFilterTableState] = useState([]);
  // table search text (cargo-mate table view)
  const [searchText, setSearchText] = useState("");

  // Message panel resize state
  const [messagePanelWidth, setMessagePanelWidth] = useState(360); // px
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const MIN_PANEL_WIDTH = 240;
  const MAX_PANEL_WIDTH = 800;

  // Ref for content-area-container to reset scroll position
  const contentAreaContainerRef = useRef(null);

  // Pre-compute today's normalized day string for reuse (YYYY-MM-DD)
  const todayDay = (() => normalizeDateToDay(new Date().toISOString()))();

  // Helper: normalize any date-time string to a YYYY-MM-DD day key (function declaration so it's hoisted)
  function normalizeDateToDay(value) {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (!isNaN(d)) return format(d, "yyyy-MM-dd");
    } catch {/* noop */}
    const token = value.split(/[ T]/)[0];
    return token.includes(":") ? value : token;
  }

  useEffect(()=> {

updateStore({tabTypeName: apiState.dataType})

  },[apiState])

  // Reset scroll position when switching tabs or components
  useEffect(() => {
    if (contentAreaContainerRef.current) {
      contentAreaContainerRef.current.scrollTop = 0;
    }
  }, [store.activeTab, apiState.dataType]);

  // Cleanup any listeners if component unmounts while resizing
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    let data = apiState.rows || [];
    // isCaseSummaryView from store is the committed value — safe to use here (this effect runs after render)
    const isCaseSummaryView = store.isCaseSummaryView;
    const dashStatus = (store.dashboardStatus || '').toLowerCase();
    // In case summary context, hide approved/rejected unless user clicked those count cards
    if (isCaseSummaryView && dashStatus !== 'approved' && dashStatus !== 'rejected' && dashStatus !== 'non-afr') {
      data = data.filter(row => {
        const s = (row.casestatus || '').toLowerCase();
        return s !== 'approved' && s !== 'rejected' && s !== 'non-afr';
      });
    }
    // Apply structured filters first
    if (Object.keys(filters)?.length > 0) {
      data = data.filter((row) => {
        const rowWeek = row?.createddate ? getWeek(new Date(row.createddate)) : null;
        const rowDay = row?.createddate ? normalizeDateToDay(row.createddate) : null;
        return (
          (filters.country ? row.country === filters.country : true) &&
          (filters.slaStatus ? row.slastatus === filters.slaStatus : true) &&
          (filters.createdDate ? rowDay === filters.createdDate : true) &&
          (filters.countrySLATime ? row.countryslatime === filters.countrySLATime : true) &&
          (filters.week ? String(rowWeek) === String(filters.week) : true)
        );
      });
    }
    // Apply free-text search after filters
    if (searchText && searchText.trim() !== "") {
      const q = searchText.trim().toLowerCase();
      data = data.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(q)));
    }
    setFilterTableState(data);
  }, [filters, apiState.rows, searchText, store.isCaseSummaryView, store.dashboardStatus]);

  // searchedTableRows removed; unified filtering resides in filterTableState

  const filterRowsForDashboard = (data, status) => {
    if (status === "reopened") {
      return data?.filter((row) => row.casestatus?.toLowerCase().endsWith("-reopened"));
    }
    return data?.filter((row) => row.casestatus?.toLowerCase() === status);
  };

  useEffect(() => {
    updateStore({
      caseId: "",
      initialCaseSelect: false,
      dashboardStatus: "",
      status: {
        mail: false,
        casestatus: "",
        mailContent: "",
      },
    });
    // Listen for external requests to go back to Sample Prompts (e.g., header click)
    const onBackToSamplePrompts = () => {
      handleBackToPrompts();
    };
    window.addEventListener('backToSamplePrompts', onBackToSamplePrompts);
    return () => window.removeEventListener('backToSamplePrompts', onBackToSamplePrompts);
  }, []);


  const processApiResponse = (apiData) => {
    // isCaseSummaryView: prefer an explicit flag stored in apiData (set on back navigation)
    // so that the overwritten promptQuery (set by invoice-details) doesn't break the flag.
    // For "Details" type (single case view), never set isCaseSummaryView to true.
    const isDetailsView = apiData?.type === "Details" && Array.isArray(apiData?.data) && apiData.data.length === 1;
    const caseSummaryFlag = isDetailsView
      ? false
      : (apiData && apiData.isCaseSummaryView !== undefined)
        ? !!apiData.isCaseSummaryView
        : !!(store.promptQuery && store.promptQuery.toLowerCase().includes('case summary'));
    updateStore({ isCaseSummaryView: caseSummaryFlag });

    if (!apiData || !apiData?.data || apiData?.data.length === 0) {
      setApiState((prev) => ({ 
        ...prev, 
        dataType: "NoData", 
        responseData: null,
        rows: null,
        headers: null,
        caseSummaryData: null,
        donutData: null
      }));
      return;
    }

    const { type: dataType, data, message } = apiData;
    let filteredRows = [];
    let tableHeaders = [];
    let caseSummaryData = null;
    let donutData = null;
    let responseData = null;
    let showFilterBar = true;
    let showCaseSummary = true;

    // Treat multi-case Details (BL/invoice lookup) exactly like a Table response
    const isMultiCaseDetails = (dataType === "Details" && Array.isArray(data) && data.length > 1);
    // BLTable = BL/invoice lookup result (same structure as Table but no filter bar)
    const isBLTable = dataType === "BLTable";

    if (dataType === "Dashboard" || dataType === "Table" || dataType === "BLTable" || isMultiCaseDetails) {
      // Store the current view before navigating to case details later
      const currentViewData = {
        dataType: isMultiCaseDetails ? "Table" : dataType,
        data,
        message,
        dashboardStatus: store.dashboardStatus,
        isCaseSummaryView: caseSummaryFlag, // persist so back navigation can restore it
        promptQuery: store.promptQuery, // Store the original query to restore it on back-navigation
      };
      
      updateStore({
        navigationHistory: {
          previousView: currentViewData,
          canGoBack: false // Reset back state when showing list views
        },
        prompt: false // Hide prompt panel when showing data
      });

      filteredRows = data;

      if (Array.isArray(data)) {
        // BLTable type = BL/invoice lookup from backend — never show filter bar
        showFilterBar = !isBLTable;

        let summaryTableHeader = [...defaultSummaryTableHeader];

        if (
          data.every(
            (row) =>
              row.casestatus?.toLowerCase() === "compliant" ||
              row.casestatus?.toLowerCase() === "open"
          )
        ) {
          summaryTableHeader = summaryTableHeader.filter(
            (key) => key !== "casestatusreason" && key !== "remarks"
          );
        }

        if (
          data.every((row) => row.casestatus?.toLowerCase() === "non-compliant")
        ) {
          summaryTableHeader = summaryTableHeader.filter(
            (key) => key !== "remarks"
          );
        }

        tableHeaders = summaryTableHeader
          .filter((key) => data[0] && data[0][key] !== undefined)
          .map((key) => ({
            key,
            header: formatHeader(key),
          }));

        const caseStatusCounts = data.reduce((acc, row) => {
          const status = row.casestatus?.toLowerCase();
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        caseSummaryData = {
          total: data.length,
          open: caseStatusCounts.open || 0,
          compliant: caseStatusCounts.compliant || 0,
          nonCompliant: caseStatusCounts["non-compliant"] || 0,
          approved: caseStatusCounts.approved || 0,
          rejected: caseStatusCounts.rejected || 0,
          onHold: caseStatusCounts["on-hold"] || 0,
          exception: caseStatusCounts.exception || 0,
        };

        // Hide CaseSummary/donut only for single-status filtered queries (e.g. "show approved cases")
        // Always show for BL/invoice lookups even if all rows happen to share one status
        const nonZeroStatuses = Object.values(caseStatusCounts).filter(v => v > 0);
        showCaseSummary = isBLTable || nonZeroStatuses.length > 1;

        donutData = [
          { name: "Open", value: caseStatusCounts.open || 0 },
          { name: "Compliant", value: caseStatusCounts.compliant || 0 },
          {
            name: "Non Compliant",
            value: caseStatusCounts["non-compliant"] || 0,
          },
          { name: "Approved", value: caseStatusCounts.approved || 0 },
          { name: "Rejected", value: caseStatusCounts.rejected || 0 },
          { name: "On-Hold", value: caseStatusCounts["on-hold"] || 0 },
          { name: "Exception", value: caseStatusCounts.exception || 0 },
        ];

  const selectedStatus = store.dashboardStatus;
  const isCaseSummaryContext = caseSummaryFlag; // use local var — store.isCaseSummaryView is not yet updated (async)
  // In case summary context, hide approved/rejected by default; show them only when user clicks those count cards
  if (isCaseSummaryContext && selectedStatus !== 'approved' && selectedStatus !== 'rejected' && selectedStatus !== 'non-afr') {
    filteredRows = filteredRows.filter(r => {
      const s = (r.casestatus || '').toLowerCase();
      return s !== 'approved' && s !== 'rejected' && s !== 'non-afr';
    });
  }
  const uniqueCountries = [...new Set(data?.map((row) => row.country))];
  const uniqueSLAStatuses = [...new Set(data?.map((row) => row.slastatus))];
  const uniqueCreatedDates = [...new Set(data?.map((row) => normalizeDateToDay(row.createddate)))];
  const uniqueCountrySLATimes = [...new Set(data?.map((row) => row.countryslatime))];
  // Week options from createddate
  const uniqueWeeks = [...new Set(data?.map((row) => {
    if(!row.createddate) return null;
    try { return getWeek(new Date(row.createddate)); } catch { return null; }
  }))];

  setCountryOptions(uniqueCountries.filter(Boolean));
  setSLAStatusOptions(uniqueSLAStatuses.filter(Boolean));
  let dayOptions = uniqueCreatedDates.filter(Boolean);
  // Ensure Today appears (at the top) even if no rows for today
  if (todayDay && !dayOptions.includes(todayDay)) {
    dayOptions.unshift(todayDay);
  }
  setCreatedDateOptions(dayOptions);
  setCountrySLATimeOptions(uniqueCountrySLATimes.filter(Boolean));
  setWeekOptions(uniqueWeeks.filter((w) => w !== null));
  setFilterTableState(filteredRows);
  if (selectedStatus) {
          // Filter rows by selected dashboard status
          filteredRows = filterRowsForDashboard(data, selectedStatus);

          // Adjust headers based on status (same logic as before)
          switch (selectedStatus) {
            case "open":
            case "compliant":
              tableHeaders = summaryTableHeader
                .filter((key) => key !== "casestatusreason" && key !== "remarks")
                .map((key) => ({
                  key,
                  header: formatHeader(key),
                }));
              break;
            case "non-compliant":
              tableHeaders = summaryTableHeader
                .filter((key) => key !== "remarks")
                .map((key) => ({
                  key,
                  header: formatHeader(key),
                }));
              break;
            case "on-hold":
            case "exception":
            default:
              tableHeaders = summaryTableHeader
                .filter((key) => data[0] && data[0][key] !== undefined)
                .map((key) => ({
                  key,
                  header: formatHeader(key),
                }));
              break;
          }
        }
      }
    } else if (dataType === "Details") {
      // Single case – show case detail view
      updateStore({
        navigationHistory: {
          ...store.navigationHistory,
          canGoBack: true
        }
      });

      if (data && data.length > 0) {
        responseData = data[0];

        tableHeaders = defaultSummaryTableHeader
          .filter((key) => data[0] && data[0][key] !== undefined)
          .map((key) => ({
            key,
            header: formatHeader(key, "lowercase"),
          }));

        const validationStatusData = transformValidationStatusData(data);
        updateStore({ validationStatusData });
      }
    }

    const extractedDate = extractDate(message);
    const todayDate = new Date().toISOString();

    // Multi-case Details and BLTable are rendered as "Table" in the UI
    const effectiveDataType = (isMultiCaseDetails || isBLTable) ? "Table" : dataType;

    setApiState({
      caseSummaryData,
      donutData,
      rows: filteredRows,
      headers: tableHeaders,
      date: extractedDate || formatDateAndTime(todayDate),
      responseData,
      dataType: effectiveDataType,
      showFilterBar,
      showCaseSummary,
    });
  };

  // Resize handlers
  function handleMouseDown(e) {
    e.preventDefault();
    isResizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = messagePanelWidth;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }

  function handleMouseMove(e) {
    if (!isResizingRef.current) return;
    const dx = e.clientX - startXRef.current;
    const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidthRef.current + dx));
    setMessagePanelWidth(newWidth);
  }

  function handleMouseUp() {
    isResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }

  // Touch events for mobile/tablet
  function handleTouchStart(e) {
    const touch = e.touches[0];
    if (!touch) return;
    isResizingRef.current = true;
    startXRef.current = touch.clientX;
    startWidthRef.current = messagePanelWidth;
    document.body.style.userSelect = 'none';
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
  }

  function handleTouchMove(e) {
    if (!isResizingRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - startXRef.current;
    const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidthRef.current + dx));
    setMessagePanelWidth(newWidth);
  }

  function handleTouchEnd() {
    isResizingRef.current = false;
    document.body.style.userSelect = '';
    window.removeEventListener('touchmove', handleTouchMove);
    window.removeEventListener('touchend', handleTouchEnd);
  }

  // keyboard accessibility for the handle
  function handleHandleKeyDown(e) {
    const step = 16; // px per arrow press
    if (e.key === 'ArrowLeft') {
      setMessagePanelWidth((w) => Math.max(MIN_PANEL_WIDTH, w - step));
    } else if (e.key === 'ArrowRight') {
      setMessagePanelWidth((w) => Math.min(MAX_PANEL_WIDTH, w + step));
    } else if (e.key === 'Home') {
      setMessagePanelWidth(MIN_PANEL_WIDTH);
    } else if (e.key === 'End') {
      setMessagePanelWidth(MAX_PANEL_WIDTH);
    }
  }

  useEffect(() => {
    if (apiResponseData && apiResponseData !== null) {
      processApiResponse(apiResponseData);
    } else if (apiResponseData === null) {
      // When apiResponseData is explicitly cleared (e.g., when clicking a new prompt),
      // clear the apiState to prevent showing stale data
      setApiState({
        caseSummaryData: null,
        donutData: null,
        rows: null,
        headers: null,
        date: null,
        responseData: null,
        dataType: null, // Don't set to "NoData", just null so loader shows
      });
    }
  }, [apiResponseData, store.dashboardStatus]);

  // Ensure cached previous view rows are patched as soon as status updates (parent-level safeguard)
  useEffect(() => {
    const caseId = (store.caseId || '').toString().trim();
    const status = (store.detailsStatus || '').toString().trim();
    const modifiedBy = (store.lastModifiedBy || '').toString().trim();
    const reason = store.caseStatusReason;
    const prev = store?.navigationHistory?.previousView;
    if (!caseId || !status || !prev || !Array.isArray(prev.data)) return;
    try {
      const updatedRows = prev.data.map((row) => {
        const rowId = row?.casenumber || row?.case_number;
        if (String(rowId) === String(caseId)) {
          return {
            ...row,
            casestatus: status || row.casestatus,
            modifiedby: modifiedBy || row.modifiedby,
            casestatusreason: (reason !== undefined ? reason : row.casestatusreason)
          };
        }
        return row;
      });
      updateStore({
        navigationHistory: {
          ...store.navigationHistory,
          previousView: { ...prev, data: updatedRows },
        },
      });
    } catch (e) {
      // noop
    }
  }, [store.detailsStatus, store.caseId, store.caseStatusReason, store.lastModifiedBy]);

  const handleBackNavigation = () => {
    if (store.navigationHistory.canGoBack && store.navigationHistory.previousView) {
      const previousView = store.navigationHistory.previousView;

      const currentCaseId = store.caseId;
      const updatedStatus = (store.detailsStatus || '').toString().trim();
      const updatedBy = (store.lastModifiedBy || '').toString().trim();

      // Restore previous dashboard status. Clear promptQuery so that the
      // message-list useEffect cannot fire a stale query (e.g. after
      updateStore({
        dashboardStatus: previousView.dashboardStatus || "",
        caseId: "",
        initialCaseSelect: false,
        promptQuery: "",
        navigationHistory: {
          previousView: null,
          canGoBack: false
        },
      });

      // Patch the previous view's data with the updated case status before restoring
      let patchedData = Array.isArray(previousView.data) ? previousView.data : [];
      if (currentCaseId && updatedStatus) {
        patchedData = patchedData.map(row => {
          const rowId = row?.casenumber || row?.case_number;
          if (String(rowId) === String(currentCaseId)) {
            return {
              ...row,
              casestatus: updatedStatus || row.casestatus,
              modifiedby: updatedBy || row.modifiedby,
              casestatusreason: (store.caseStatusReason !== undefined ? store.caseStatusReason : row.casestatusreason)
            };
          }
          return row;
        });
      }

      // Preserve existing filters/search from the earlier view; do not override

      // Simulate the previous API response to restore the earlier view with patched rows
      updateStore({
        // Also restore the isCaseSummaryView flag BEFORE processApiResponse runs
        isCaseSummaryView: !!(previousView.isCaseSummaryView),
        apiResponseData: {
          // Restore the exact previous view type (Dashboard/Table)
          type: previousView.dataType,
          data: patchedData,
          message: previousView.message,
          isCaseSummaryView: !!(previousView.isCaseSummaryView), // carry through to processApiResponse
        }
      });
    } else {
      // If there's no previous view (e.g., user directly searched for a case),
      // go back to sample prompts
      handleBackToPrompts();
    }
  };

  const handleBackToPrompts = () => {
    // Clear API response data and show prompt panel
    updateStore({
      apiResponseData: null,
      prompt: true,
      loading: false,
      dashboardStatus: "",
      caseId: "",
      initialCaseSelect: false,
      selectedAssignees: [],
      isCaseSummaryView: false,
      navigationHistory: {
        previousView: null,
        canGoBack: false
      }
    });
    
    // Reset API state
    setApiState({
      caseSummaryData: null,
      donutData: null,
      rows: null,
      headers: null,
      date: null,
      responseData: null,
      dataType: null,
    });
    // Clear table search text when leaving the table/dashboard view
    setSearchText("");
  };

  const handleRefresh = () => {
    const queryToRefresh = store.lastRefreshableQuery || store.promptQuery;
    if (!queryToRefresh) return;
    updateStore({
      promptQuery: queryToRefresh,
      promptQueryNonce: (store.promptQueryNonce || 0) + 1,
      prompt: false,
      loading: true,
      apiResponseData: null,
      dashboardStatus: "",
    });
    // Reset filters and search text for a clean refresh
    setFilters({});
    setSearchText("");
  };

  // Track previous prompt query to clear search and assignee filter when a new prompt is issued
  const prevPromptRef = useRef(store.promptQuery);
  useEffect(() => {
    if (prevPromptRef.current !== store.promptQuery) {
      setSearchText("");
      updateStore({ selectedAssignees: [] });
      prevPromptRef.current = store.promptQuery;
    }
  }, [store.promptQuery]);

  // Clear search when data type changes away from Table (e.g., Dashboard, Details, NoData)
  useEffect(() => {
    if (apiState.dataType && apiState.dataType !== 'Table' && searchText) {
      setSearchText("");
    }
  }, [apiState.dataType]);
  
  const renderComponent = () => {
    const { activeTab, loading } = store;
    

    // If Case Activity Log tab is active, show the Advanced Case Search component
  if (activeTab === 'advanced-search') {
      return <AdvancedCaseSearch />;
    }

    if (activeTab === 'metrics-snapshot') {
      return <MetricsSnapshot />;
    }
    // Show loading indicator when loading
    if (loading) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          flexDirection: 'column' 
        }}>
          <McLoadingIndicator
            appearance="primary"
            style={{ fontSize: "36px", transform: "scale(2)", marginBottom: "16px" }}
          />
          {/* <span style={{ fontSize: "16px", color: "#666" }}>Loading your data...</span> */}
        </div>
      );
    }

    // For Cargo Mate tab or when there's no specific tab data, show the regular content
    switch (apiState.dataType) {
      case "NoData":
        return (
          <div className="data-view-container">
            <div className="no-data-header-with-back">
              <div className="back-button-container">
                <McIcon
                  icon="chevron-left"
                  size="24"
                  onClick={handleBackToPrompts}
                  className="back-button-icon"
                  style={{ cursor: 'pointer' }}
                />
                <span 
                  onClick={handleBackToPrompts}
                  className="back-button-text"
                  style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '14px', fontWeight: '500' }}
                >
                  Back to Sample Prompts
                </span>
                <McButton label="Refresh" appearance="secondary" fit="small" 
                  icon="arrow-clockwise" click={handleRefresh} 
                  style={{ marginLeft: 'auto' }}>
                </McButton>
              </div>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              textAlign: 'center'
            }}>
            <McIcon
              icon="search"
              size="48"
              style={{ color: '#ccc', marginBottom: '16px' }}
            />
            <h3 style={{ color: '#666', marginBottom: '8px' }}>No Records Found</h3>
            <p style={{ color: '#999', fontSize: '14px' }}>
              {SUMMARY.NO_RECORDS_FOUND}
            </p>
            </div>
          </div>
        );
      case "Dashboard":
        return (
          <div className="data-view-container">
            <div className="dashboard-header-with-back">
              <div className="back-button-container">
                <McIcon
                  icon="chevron-left"
                  size="24"
                  onClick={handleBackToPrompts}
                  className="back-button-icon"
                  style={{ cursor: 'pointer' }}
                />
                <span 
                  onClick={handleBackToPrompts}
                  className="back-button-text"
                  style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '14px', fontWeight: '500' }}
                >
                  Back to Sample Prompts
                </span>
                <McButton label="Refresh" appearance="secondary" fit="small" 
                  icon="arrow-clockwise" click={handleRefresh} 
                  style={{ marginLeft: 'auto' }}>
                </McButton>
              </div>
            </div>
            <Dashboard apiState={apiState} />
          </div>
        );
      case "Details":
        return <CaseDetails caseDetailsData={apiState.responseData} onBackClick={handleBackNavigation} />;
      case "Table":
        return (
          <div className="data-view-container">
              <div className="table-header-with-back">
                <div className="back-button-container">
                  <McIcon
                    icon="chevron-left"
                    size="24"
                    onClick={handleBackToPrompts}
                    className="back-button-icon"
                    style={{ cursor: "pointer" }}
                  />
                  <span
                    onClick={handleBackToPrompts}
                    className="back-button-text"
                    style={{
                      cursor: "pointer",
                      marginLeft: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Back to Sample Prompts
                  </span>
                  <McButton label="Refresh" appearance="secondary" fit="small" 
                    icon="arrow-clockwise" click={handleRefresh} 
                    style={{ marginLeft: 'auto' }}>
                  </McButton>
                </div>
              </div>
              <div className="filterBar" style={{ display: apiState.showFilterBar !== false ? '' : 'none' }}>
                <div className="filterBar-row">
                  <McSelect
                    label="Country"
                    placeholder="Select country"
                    name="country"
                    value={filters.country || ""}
                    optionselected={(e) => {
                      const val = e.target.value;
                      setFilters((prev) => ({ ...prev, country: val }));
                      updateStore({ selectedCountry: val });
                    }}
                  >
                    {countryOptions.map((country) => (
                      <McOption key={country} value={country}>
                        {country}
                      </McOption>
                    ))}
                  </McSelect>
                  <McSelect
                    label="SLA Status"
                    placeholder="Select SLA Status"
                    name="slaStatus"
                    value={filters.slaStatus || ""}
                    optionselected={(e) => setFilters((prev) => ({ ...prev, slaStatus: e.target.value }))}
                  >
                    {slaStatusOptions.map((status) => (
                      <McOption key={status} value={status}>
                        {status}
                      </McOption>
                    ))}
                  </McSelect>
                  <McSelect
                    label="Country SLA Time"
                    placeholder="Select Country SLA Time"
                    name="countrySLATime"
                    value={filters.countrySLATime || ""}
                    optionselected={(e) => setFilters((prev) => ({ ...prev, countrySLATime: e.target.value }))}
                  >
                    {countrySLATimeOptions.map((time) => (
                      <McOption key={time} value={time}>
                        {time}
                      </McOption>
                    ))}
                  </McSelect>
                  <McSelect
                    label="Week"
                    placeholder="Select Week"
                    name="week"
                    value={filters.week || ""}
                    optionselected={(e) => setFilters((prev) => ({ ...prev, week: e.target.value }))}
                  >
                    {weekOptions.map((week) => (
                      <McOption key={week} value={week}>
                        {week}
                      </McOption>
                    ))}
                  </McSelect>
                  <McSelect
                    label="Created Date"
                    placeholder="Select Created Date"
                    name="createdDate"
                    value={filters.createdDate || ""}
                    optionselected={(e) => setFilters((prev) => ({ ...prev, createdDate: e.target.value }))}
                  >
                    {createdDateOptions.map((day) => {
                      const displayDate = (() => {
                        if (!day) return "";
                        try {
                          const parsed = new Date(day);
                          if (!isNaN(parsed)) {
                            if (day === todayDay) return `Today (${format(parsed, "dd MMM yyyy")})`;
                            return format(parsed, "dd MMM yyyy");
                          }
                        } catch { /* ignore */ }
                        return day;
                      })();
                      return (
                        <McOption key={day} value={day}>
                          {displayDate}
                        </McOption>
                      );
                    })}
                  </McSelect>
                  <McButton
                    appearance="primary"
                    fit="medium"
                    click={() => {
                      setFilters({});
                      updateStore({ selectedCountry: "" });
                    }}
                  >
                    Clear
                  </McButton>
                </div>
                {/* <div className="filterBar-search">
                  <input
                    type="text"
                    placeholder="Search table..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div> */}
              </div>
              {apiState.caseSummaryData && apiState.showCaseSummary !== false && !store.dashboardStatus && (
                <CaseSummary apiState={apiState} />
              )}
              <CustomTable headers={apiState.headers} rows={filterTableState} />
          </div>
        );
      default:
        // Only show PromptPanel if we're on cargo-mate tab AND prompt flag is explicitly true
        // This prevents flash of PromptPanel when loading is briefly false during state transitions
        if (store.activeTab === 'cargo-mate' && store.prompt === true) {
          return <PromptPanel />;
        }
        // If we're loading or waiting for data, show nothing (loader will show above)
        // If we're on another tab, show nothing
        return null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', flex: '1 1 0', minHeight: 0 }}>
      {store.activeTab === 'cargo-mate' && (
        <div 
          className="layout-container"
          style={{ width: store.showMessagePanel ? `${messagePanelWidth}px` : 'auto' }}
        >
          {store.showMessagePanel && (
            <div
              className="message-area resizable"
            >
              <MessageList />
              <div
                className="message-area-handle"
                role="separator"
                tabIndex={0}
                aria-orientation="vertical"
                aria-valuenow={messagePanelWidth}
                aria-valuemin={MIN_PANEL_WIDTH}
                aria-valuemax={MAX_PANEL_WIDTH} 
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onKeyDown={handleHandleKeyDown}
                title="Resize chat panel"
              />
            </div>
          )}
          {!store.showMessagePanel && (
            <div className="chat-toggle-button">
              <McIcon
                icon="chatbot"
                size="24"
                onClick={() => updateStore({ showMessagePanel: true })}
                className="chat-toggle-icon"
                color="#FFFFFF"
              />
            </div>
          )}
        </div>
      )}
    <div className="content-area-container" ref={contentAreaContainerRef}>
      <div className="content-area">
        {renderComponent()}
      </div>
    </div>
    </div>
  );
};

export default Home;
